import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { BehaviorSubject, forkJoin, Observable, of, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { GeneralConfirmationComponent } from '../shared-components/controls/general-confirmation/general-confirmation.component';
import { Portal } from '../shared-components/portal.model';
import { PermissionEnum } from '../shared-components/security/permissions.enums';
import { SecurityService } from '../shared-components/security/security.service';
import { SharedService } from '../shared-components/shared.service';
import { StudentService } from '../shared-components/student/student.service';
import { System } from '../shared-components/System';
import { UserPreferencesService } from '../shared-components/user-preferences/user-preferences.service';
import { UserSchoolYearPolicies } from '../shared-components/user/user-school-year-policies.model';
import { UserService } from '../shared-components/user/user.service';
import { YearService } from '../shared-components/year/year.service';
import { LoginService } from '../shared-modules/login/login.service';
import { OnlinePaymentConfiguration } from '../student360/online-payment/models/online-payment-config.model';
import { ClassroomAttendance } from './attendance/models/attendance.model';
import { StudentPeriodicAttendanceDetail } from './attendance/models/student-periodic-attendance-detail.model';
import { ClassPostingStatus, CurrentGradingCycleInfo, GradingCycleClassPostingGrade, GradingCycleCValidGrades, GragingCycles, StudentsWithoutTermRecords, ViewGradingCycleGrades } from './grading-cycles/graging-cycles.model';
import { NewStudent, Sites } from './new-students-alert-dialog/model';

@Injectable()
export class Classroom360Service {

    defaultYearSubscription: Subscription;
    selectedStudentSubscription: Subscription;

    public staffAssignedPortals: Portal[];
    observableStaffAssignedPortals = new BehaviorSubject<Portal[]>([]);
    observableStaffAssignedPortalsStream$ = this.observableStaffAssignedPortals.asObservable();
    gradebookPortalSelected: boolean;
    attendancePortalSelected: boolean;
    courseInfoPortalSelected: boolean;
    previousPortalArgs: { [key: string]: any } = {};
    redirectToEForms: boolean;
    performed = false;
    V10: boolean;
    currentGradingCycleInfo: CurrentGradingCycleInfo = new CurrentGradingCycleInfo();
    private _schoolPolicy: UserSchoolYearPolicies;
    observableSchoolPolicy = new BehaviorSubject<UserSchoolYearPolicies>(new UserSchoolYearPolicies());
    observableSchoolPolicyStream$ = this.observableSchoolPolicy.asObservable();
    get schoolPolicy(): UserSchoolYearPolicies {
        return this._schoolPolicy;
    }
    set schoolPolicy(value: UserSchoolYearPolicies) {
        this._schoolPolicy = value;
        this.observableSchoolPolicy.next(value);
    }

    // TODO: this is for discovering inactive modules and should be removed in the future
    S360CourseRequestPages = [
        '/scheduling/districtCourseRequestSetup',
        '/scheduling/schoolCourseRequestSetup'
    ];
    S360ClassSchedulingPages = [
        '/scheduling/classesByCourse',
        '/scheduling/customSchedulingProperties',
        '/scheduling/bellSchedule/districtBellSchedule',
        '/scheduling/masterschedule',
        '/scheduling/roomSchedulingSettings',
        '/scheduling/bellSchedule/schoolBellSchedule',
        '/scheduling/schoolSchedulingFolders',
        '/scheduling/sections',
        '/scheduling/teacherSchedulingSettings',
        '/scheduling/bellSchedule/editSchoolBellSchedule',
        '/scheduling/bellSchedule/editDistrictBellSchedule',
        '/scheduling/studentSchedulingSettings'
    ];
    S360StudentSchedulingPages = [
        '/scheduling/scheduleStudentList'
    ];

    constructor(
        private http: HttpClient,
        private yearService: YearService,
        private userService: UserService,
        private userPreferencesService: UserPreferencesService,
        private studentService: StudentService,
        private securityService: SecurityService,
        private sharedService: SharedService,
        private router: Router,
        private dialog: MatDialog,
        private loginService: LoginService
    ) {
        this.V10 = loginService.LoginSettings.V10;

        const getNewStaffPortals = (studentId = 0, from = '') => {
            if (this.yearService.defaultYear && this.userService.currentUser && this.userService.currentUser.UserType != 2) {
                this.getStaffAssignedPortals(studentId).pipe(take(1)).subscribe(portals => {

                    // Updating the portals OR resetting if the user wishes to go back
                    const allowedChange = this.updateStaffAssignedPortals(portals);
                    allowedChange.pipe(take(1)).subscribe(result => {
                        if (!result) {

                            // Resetting student
                            if (from == 'Student') {
                                this.studentService.updateSelectedStudent(this.studentService.previousSelectedStudent.Id);
                            }

                            // Resetting year
                            if (from == 'Year') {
                                this.yearService.defaultYear = this.yearService.previousYear;
                            }
                        }
                    });
                });
            }
        };
        this.defaultYearSubscription = yearService.observableDefaultYearStream$.subscribe(() => {
            if (this.userService.userType == 1) {
                const studentId = this.studentService.selectedStudent ? this.studentService.selectedStudent.Id : 0;
                getNewStaffPortals(studentId, 'Year');
            }
        });
        this.selectedStudentSubscription = this.studentService.selectedStudentStream$.subscribe(studentId => {
            if (this.userService.userType == 1) {
                getNewStaffPortals(studentId, 'Student');
            }
        });
    }

    getStaffAssignedPortals(studentId: number = 0, forceGetPortals: boolean = false): Observable<Portal[]> {
        if (
            !this.yearService.defaultYear
            || (this.yearService.defaultYear.SchoolID <= 0 && (this.V10 || studentId <= 0))
            || this.yearService.defaultYear.SchoolID == null
        ) {
            return of([]);
        }
        if (this.staffAssignedPortals
            && this.staffAssignedPortals.length > 0
            && !forceGetPortals
            && (this.previousPortalArgs['studentId'] ? this.previousPortalArgs['studentId'] : 0) == (studentId ? studentId : 0) // All falsey values become 0, preventing the case of null != 0 => true
            && (this.previousPortalArgs['yearId'] ? this.previousPortalArgs['yearId'] : 0) == this.yearService.defaultYear.Id
            && (this.previousPortalArgs['siteId'] ? this.previousPortalArgs['siteId'] : 0) == this.yearService.defaultYear.SchoolID
        ) {
            return of(this.staffAssignedPortals);
        } else {
            this.previousPortalArgs['studentId'] = studentId;
            this.previousPortalArgs['yearId'] = this.yearService.defaultYear.Id;
            this.previousPortalArgs['siteId'] = this.yearService.defaultYear.SchoolID;

            //  We obtain the school policy here because we need a modal popup for when a feature has not yet been activated
            // TODO: Obtaining the school policy is temporary and should be removed in the future
            return forkJoin(
                this.http.get<Portal[]>(System.apiURL.Classroom + 'GetStaffAssignedPortals?schoolId=' + this.yearService.defaultYear.SchoolID + '&yearId=' + this.yearService.defaultYear.Id + '&studentId=' + studentId),
                this.userService.getUserSchoolYearPolicies(this.yearService.defaultYear.Id, this.yearService.defaultYear.SchoolID)
            ).pipe(
                map(results => {

                    // Setting the respective properties
                    const portals = results[0];
                    this.schoolPolicy = results[1];

                    // We need to separate the portals in multiple groups into separate portals because GroupName is a CSV
                    for (let i = 0; i < portals.length; i++) {

                        // If multiple groups are found, split the portals
                        if (portals[i].GroupName.includes(',')) {
                            const groups = portals[i].GroupName.split(',');
                            const portalDuplicates = [];
                            for (let j = 0; j < groups.length; j++) {
                                groups[j] = groups[j].trim();

                                // If this is the first element, alter our current portal
                                if (j == 0) {
                                    portals[i].GroupName = groups[0];
                                    if (this.V10) {
                                        portals[i].IsFavorite = portals[i].GroupNameFavorite && portals[i].GroupNameFavorite.includes(groups[0]);
                                    }
                                    portalDuplicates.push(portals[i]);
                                }

                                // After the first element, create new portals
                                else {
                                    const portalInGroup = new Portal();
                                    Object.assign(portalInGroup, portals[i]);
                                    portalInGroup.GroupName = groups[j];
                                    if (this.V10) {
                                        portalInGroup.IsFavorite = portals[i].GroupNameFavorite && portalInGroup.GroupNameFavorite.includes(portalInGroup.GroupName);
                                    }
                                    portals.push(portalInGroup);
                                    portalDuplicates.push(portalInGroup);
                                }
                            }

                            // The first one that isn't student group should show as tile
                            if (portals[i].ShowAsTile && portalDuplicates.length > 0) {
                                let showAsTileSet = false;
                                for (let j = 0; j < portalDuplicates.length; j++) {
                                    if (!showAsTileSet && !portalDuplicates[j].GroupName.toLowerCase().includes('student')) {
                                        portalDuplicates[j].ShowAsTile = true;
                                        showAsTileSet = true;
                                    } else {
                                        portalDuplicates[j].ShowAsTile = false;
                                    }
                                }
                            }
                        }
                    }

                    if (this.userPreferencesService.favoritesPreferences) {
                        for (let i = 0; i < portals.length; i++) {
                            const index: number = this.userPreferencesService.favoritesPreferences.FavoritePortals.findIndex(x =>
                            (x.PortalName == portals[i].PortalName  && x.PortalModule == portals[i].ParentModule &&
                                (x.GroupName == portals[i].GroupName
                                    || (x.GroupName == 'Students' && portals[i].GroupName == 'My Students')
                                    || (x.GroupName == 'Classes' && portals[i].GroupName == 'My Classes')
                                    || (x.GroupName == 'My Students' && portals[i].GroupName == 'Students')
                                    || (x.GroupName == 'My Classes' && portals[i].GroupName == 'Classes')
                                ))
                            );
                            portals[i].Selected = index != -1 &&
                                (this.userPreferencesService.favoritesPreferences.FavoritePortals[index].GroupName == portals[i].GroupName
                                    || (this.userPreferencesService.favoritesPreferences.FavoritePortals[index].GroupName == 'Students' && portals[i].GroupName == 'My Students')
                                    || (this.userPreferencesService.favoritesPreferences.FavoritePortals[index].GroupName == 'Classes' && portals[i].GroupName == 'My Classes')
                                    || (this.userPreferencesService.favoritesPreferences.FavoritePortals[index].GroupName == 'My Students' && portals[i].GroupName == 'Students')
                                    || (this.userPreferencesService.favoritesPreferences.FavoritePortals[index].GroupName == 'My Classes' && portals[i].GroupName == 'Classes')
                                ) &&
                                this.userPreferencesService.favoritesPreferences.FavoritePortals[index].PortalModule == portals[i].ParentModule;
                        }
                    }

                    this.staffAssignedPortals = Array.prototype.sort.call(portals, (a, b) => a.PortalName.localeCompare(b.PortalName));
                    return _.cloneDeep(this.staffAssignedPortals);
                })
            );
        }
    }

    getOnlinePaymentConfiguration(): Observable<OnlinePaymentConfiguration> {
        return this.sharedService.getOnlinePaymentConfiguration();
    }

    public getStudentsForAttendance(objYearId: number, schoolId: number, siteSectionId: number, defaultDate: string, showStudentPhoto: boolean, showSeatingChart: boolean): Observable<ClassroomAttendance[]> {
        let queryString = '';
        queryString += '?yearId=' + objYearId +
            '&schoolId=' + schoolId +
            '&siteSectionId=' + siteSectionId +
            '&currentDate=' + defaultDate +
            '&showStudentPhoto=' + showStudentPhoto +
            '&showSeatingChart=' + showSeatingChart;
        return this.http.get<ClassroomAttendance[]>(System.apiURL.Classroom + 'GetStudentsForAttendance' + queryString);
    }

    public getStudentsForAttendanceForDate(objYearId: number, schoolId: number, siteSectionId: number, attendanceDate: string, showStudentPhoto: boolean, showSeatingChart: boolean): Observable<ClassroomAttendance[]> {
        let queryString = '';
        queryString += '?yearId=' + objYearId +
            '&schoolId=' + schoolId +
            '&siteSectionId=' + siteSectionId +
            '&attendanceDate=' + attendanceDate +
            '&showStudentPhoto=' + showStudentPhoto +
            '&showSeatingChart=' + showSeatingChart;
        return this.http.get<ClassroomAttendance[]>(System.apiURL.Classroom + 'GetStudentsForAttendanceForDate' + queryString);
    }

    public getStudentPeriodicAttendanceDetail(studentId: number, schoolId: number, AYIdentifier, AttDate: string): Observable<StudentPeriodicAttendanceDetail[]> {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&schoolId=' + schoolId +
            '&AYIdentifier=' + AYIdentifier +
            '&AttDate=' + AttDate;
        return this.http.get<StudentPeriodicAttendanceDetail[]>(System.apiURL.Classroom + 'GetStudentPeriodicAttendanceDetail' + queryString);
    }

    public updateStudentAttendance(schoolId: number, yearId: number, userId: number, data: ClassroomAttendance[], attDate: string, siteSectionId: number, markPerfectAttendance: boolean): Observable<any> {
        let queryString = '';
        queryString += '?schoolId=' + schoolId +
            '&yearId=' + yearId +
            '&userId=' + userId +
            '&attDate=' + attDate +
            '&siteSectionId=' + siteSectionId +
            '&markPerfectAttendance=' + markPerfectAttendance;
        return this.http.post(System.apiURL.Classroom + 'UpdateStudentAttendance' + queryString, data);
    }

    public massUpdateStudentAttendanceByClass(schoolId: number, yearId: number, userId: number, data: ClassroomAttendance[], attDate: string, siteSectionId: number, markPerfectAttendance: boolean): Observable<any> {
        let queryString = '';
        queryString += '?schoolId=' + schoolId +
            '&yearId=' + yearId +
            '&userId=' + userId +
            '&attDate=' + attDate +
            '&siteSectionId=' + siteSectionId +
            '&markPerfectAttendance=' + markPerfectAttendance;
        return this.http.post(System.apiURL.Classroom + 'MassUpdateStudentAttendanceByClass' + queryString, data);
    }

    updateStaffAssignedPortals(portals: Portal[]): Observable<boolean> {
        // POST: updates the list of assigned portals to the supplied portals, showing a warning dialog if the current page is not valid

        // Checking if we have permissions to view the current page in the new portals
        let url = this.router.url;
        const queryParamIndex = url.indexOf('?');
        if (queryParamIndex >= 0) {
            url = url.substring(0, queryParamIndex);
        }
        let hasPortal = false;

        // Hard-coded URLs shouldn't be checked because there is nothing to check against
        if (
            url.toLowerCase().includes('login')
            || url.toLowerCase().includes('/classroom360/classroomsummary')
            || url.toLowerCase().includes('/classroom360/cards')
            || url.toLowerCase().includes('/student360/studentsummary')
            || url.toLowerCase().includes('/eforms/submissions')
            || url.toLowerCase().includes('/changepassword')
            || url.toLowerCase().includes('/editschoolbellschedule')
            || url.toLowerCase().includes('/editdistrictbellschedule')
            || url.toLowerCase().includes('/registrationenrollment/detailedregistration')
            || url.toLowerCase().includes('/student360/eformsstaff/:staffpage')
            || url.toLowerCase().includes('/healthvisitsummary/healthvisitsummaryondate')
        ) {
            hasPortal = true;
        }

        // For all other portals
        else {
            portals.forEach(portal => {
                if ((portal.ParentModule + portal.PortalDescription).toLowerCase() == url.toLowerCase()) {
                    hasPortal = true;
                }
            });
        }
        // Programs and services does not have a portal in v10
        // TODO: This is a hacky fix and we need to consider adding a permission checker in the router
        if (url.toLowerCase().includes('programsandservices')) {
            if (this.loginService.LoginSettings.V10 && this.securityService.IsAnyPermissionAssigned(PermissionEnum.StudentGeneralProfileStudentPrograms)) {
                hasPortal = true;
            }
        }

        // If we have permissions, just continue
        if (hasPortal) {
            this.staffAssignedPortals = portals;
            this.observableStaffAssignedPortals.next(portals);
            return of(true);
        }

        // If we do not have permissions, inform the user
        else {
            let dialogBox;
            // We show a "feature has not yet been enabled" here because scheduling can be activate or deactivated
            // TODO: This is temporary and should be removed in the future
            const counselorIndex = url.indexOf('/counselor360');
            let counselorPageUrl = url;
            if (counselorIndex >= 0) {
                counselorPageUrl = url.substring(counselorIndex + '/counselor360'.length, url.length);
            }
            if (
                counselorIndex >= 0
                && this.schoolPolicy
                && (
                    (
                        this.S360CourseRequestPages.includes(counselorPageUrl)
                        && !this.schoolPolicy.S360SchedulingCourseRequests
                    )
                    || (
                        this.S360ClassSchedulingPages.includes(counselorPageUrl)
                        && !this.schoolPolicy.S360SchedulingClassScheduling
                    )
                    || (
                        this.S360StudentSchedulingPages.includes(counselorPageUrl)
                        && !this.schoolPolicy.S360SchedulingStudentScheduling
                    )
                )
            ) {
                dialogBox = this.dialog.open(GeneralConfirmationComponent, {
                    disableClose: true,
                    data: {
                        headerLabel: 'Module Not Activated',
                        confirmMessage: 'The module has not been activated for this school and year. If you continue, you will be redirected to the Classroom Summary.',
                        okButtonLabel: 'Continue',
                        warning: true,
                        informational: (url.toLowerCase() == '/eforms/submissions' || url.toLowerCase() == '/admin360/eForms/processing') ? true : false,
                        Url: url.toLowerCase()
                    }
                });
            }

            // If this is not a counselor page or the school policy is active, then the user does not have proper permissions
            else {
                dialogBox = this.dialog.open(GeneralConfirmationComponent, {
                    disableClose: true,
                    data: {
                        headerLabel: 'Insufficient Permissions',
                        confirmMessage: (this.userService.userType == 1 && !this.loginService.LoginSettings.V10 && (url.toLowerCase().includes('/eforms/submissions') || url.toLowerCase().includes('/admin360/eForms/processing'))) ? ('You do not have permission to view the current page for the selected year.') : ('You do not have permission to view the current page for this student, school, and year. If you continue, you will be redirected to the Classroom Summary.'),
                        okButtonLabel: 'Continue',
                        warning: true,
                        informational: (url.toLowerCase() == '/eforms/submissions' || url.toLowerCase() == '/admin360/eForms/processing') ? true : false,
                        Url: url.toLowerCase()
                    }
                });
            }
            // Responding to results
            if (this.userService.userType == 1 && !this.loginService.LoginSettings.V10 && (url.toLowerCase().includes('/eforms/submissions') || url.toLowerCase().includes('/admin360/eForms/processing'))) {
                return;
            } else {
                return dialogBox.afterClosed().pipe(
                    take(1),
                    map(result => {


                        // If the user continues, set the portals and redirect to the classroom summary
                        if (result) {
                            this.staffAssignedPortals = portals;
                            this.observableStaffAssignedPortals.next(portals);
                            this.router.navigateByUrl('/classroom360/cards');
                        }

                        // Return the result for processing elsewhere
                        return result;
                    })
                );
            }
        }
    }

    getNewStudentList(familyId = 0, yearId = 0): Observable<NewStudent[]> {
        let queryString = '';
        queryString += '?familyId=' + familyId +
            '&yearId=' + yearId;
        return this.http.get<NewStudent[]>(System.apiURL.Classroom + 'GetNewStudentsList' + queryString);
    }

    getSiteList(): Observable<Sites[]> {
        return this.http.get<Sites[]>(System.apiURL.Classroom + 'GetSiteList');
    }

    getGradingCyclesBySiteYearId(siteYearId: number): Observable<GragingCycles[]> {
        let queryString = '';
        queryString += '?siteYearId=' + siteYearId;
        return this.http.get<GragingCycles[]>(System.apiURL.Classroom + 'GetGradeCyclesBySiteYearId' + queryString);
    }

    public updateGradingCycle(siteYearId: number, bytAction: number, gradindCycles: GragingCycles[]): Observable<any> {
        let queryString = '';
        queryString += '?siteYearId=' + siteYearId +
            '&bytAction=' + bytAction;
        return this.http.post(System.apiURL.Classroom + 'UpdateGradingCycle' + queryString, gradindCycles);
    }

    public createNewGradeCycle(siteYearId: number, deleteAnyExistingGrades: boolean, keepNotNullGrades: boolean, createAHRecordsBased: boolean, setAsCurrent: boolean, withinDays: number, gradindCycle: GragingCycles): Observable<any> {
        let queryString = '';
        if (!withinDays) {
            withinDays = 0;
        }
        queryString += '?siteYearId=' + siteYearId +
            '&deleteAnyExistingGrades=' + deleteAnyExistingGrades +
            '&keepNotNullGrades=' + keepNotNullGrades +
            '&createAHRecordsBased=' + createAHRecordsBased +
            '&setAsCurrent=' + setAsCurrent +
            '&withinDays=' + withinDays;
        return this.http.post(System.apiURL.Classroom + 'CreateNewGradeCycle' + queryString, gradindCycle);
    }

    getClassPostingStatus(siteYearId: number, termGradeCycleId: number, sorting: number): Observable<ClassPostingStatus[]> {
        let queryString = '';
        queryString += '?siteYearId=' + siteYearId +
            '&termGradeCycleId=' + termGradeCycleId +
            '&sorting=' + sorting;
        return this.http.get<ClassPostingStatus[]>(System.apiURL.Classroom + 'GetClassPostingStatus' + queryString);
    }

    getStudentsWithoutTermRecords(siteYearId: number, gradeCycleId: number, termStartDate: Date, termStopDate: Date): Observable<StudentsWithoutTermRecords[]> {
        let queryString = '';
        queryString += '?siteYearId=' + siteYearId +
            '&gradeCycleId=' + gradeCycleId +
            '&termStartDate=' + termStartDate +
            '&termStopDate=' + termStopDate;
        return this.http.get<StudentsWithoutTermRecords[]>(System.apiURL.Classroom + 'GetStudentsWithoutTermRecords' + queryString);
    }

    saveTermGradesForStudentsWithoutTerm(siteYearId: number, termGradesPostOpen: Date, termGradesPostClose: Date, selectedRows: StudentsWithoutTermRecords[]): Observable<any> {
        let queryString = '';
        queryString += '?siteYearId=' + siteYearId +
            '&termGradesPostOpen=' + termGradesPostOpen +
            '&termGradesPostClose=' + termGradesPostClose;
        // '&selectedRows=' + selectedRows;
        return this.http.post(System.apiURL.Classroom + 'SaveTermGradesForStudentsWithoutTerm' + queryString, selectedRows);
    }

    getGradeCycleGradesByGradeCycleId(termGradeCycleId: number): Observable<ViewGradingCycleGrades[]> {
        let queryString = '';
        queryString += '?termGradeCycleId=' + termGradeCycleId;
        return this.http.get<ViewGradingCycleGrades[]>(System.apiURL.Classroom + 'GetGradeCycleGradesByGradeCycleId' + queryString);
    }

    getClassPostingGrades(siteYearId: number, termGradeCycleId: number, siteSectionId: number): Observable<GradingCycleClassPostingGrade[]> {
        let queryString = '';
        queryString += '?siteYearId=' + siteYearId +
            '&termGradeCycleId=' + termGradeCycleId +
            '&siteSectionId=' + siteSectionId;
        return this.http.get<GradingCycleClassPostingGrade[]>(System.apiURL.Classroom + 'GetClassPostingGrades' + queryString);
    }

    saveClassPostingGrades(classPostingGrades: GradingCycleClassPostingGrade[]): Observable<any> {
        const ClassPostingGrades = classPostingGrades;
        return this.http.post(System.apiURL.Classroom + 'SaveClassPostingGrades', ClassPostingGrades);
    }

    getGradingCycleStudentGrades(siteYearId: number, StudentId: number, termGradeCycleId: number, termGradeDetailTypeId: number): Observable<GradingCycleClassPostingGrade[]> {
        let queryString = '';
        queryString += '?siteYearId=' + siteYearId +
            '&StudentId=' + StudentId +
            '&termGradeCycleId=' + termGradeCycleId +
            '&termGradeDetailTypeId=' + termGradeDetailTypeId;
        return this.http.get<GradingCycleClassPostingGrade[]>(System.apiURL.Classroom + 'GetGradingCycleStudentGrades' + queryString);
    }

    getValidGradesBySiteYear(siteYearId: number): Observable<GradingCycleCValidGrades[]> {
        let queryString = '';
        queryString += '?siteYearId=' + siteYearId;
        return this.http.get<GradingCycleCValidGrades[]>(System.apiURL.Classroom + 'GetValidGradesBySiteYear' + queryString);
    }
}
